/*
 * pptx slide DSL → PptxGenJS → base64 .pptx (rendered entirely in the pane).
 *
 * The DSL is the SAME vocabulary agents author against the server-side
 * append_slides path (app.office.knowledge.pptx:authoring): a 13.333x7.5in
 * 16:9 canvas, all geometry in INCHES, colors hex WITHOUT '#', font sizes in
 * points, elements painted in array order (earlier behind later). Keep this
 * mapping in sync with that guide — it is the contract the agent follows.
 */

import PptxGenJS from "pptxgenjs";

export interface DslSlide {
  background?: { color?: string; image?: string };
  elements: any[];
  notes?: string;
}

const LAYOUT_NAME = "PF_WIDE";

/** DSL shape name → pptxgenjs shape name (pass-through for the known set). */
const SHAPE_MAP: Record<string, string> = {
  rect: "rect",
  roundRect: "roundRect",
  ellipse: "ellipse",
  line: "line",
  triangle: "triangle",
  chevron: "chevron",
  rightArrow: "rightArrow",
};

const CHART_MAP: Record<string, string> = {
  bar: "bar",
  line: "line",
  pie: "pie",
  doughnut: "doughnut",
  area: "area",
  scatter: "scatter",
  radar: "radar",
};

function shadowOf(s: any): any {
  if (!s) return undefined;
  return { type: "outer", ...s };
}

/** Text-styling options shared by text elements and shape labels. */
function textOptions(el: any): any {
  const o: any = {};
  if (el.fontSize !== undefined) o.fontSize = el.fontSize;
  if (el.fontFace) o.fontFace = el.fontFace;
  if (el.color) o.color = el.color;
  if (el.bold !== undefined) o.bold = el.bold;
  if (el.italic !== undefined) o.italic = el.italic;
  if (el.align) o.align = el.align;
  if (el.valign) o.valign = el.valign;
  if (el.lineSpacingPt !== undefined) o.lineSpacing = el.lineSpacingPt;
  if (el.charSpacing !== undefined) o.charSpacing = el.charSpacing;
  if (el.margin !== undefined) o.margin = el.margin;
  if (el.bullet !== undefined) {
    if (el.bullet === true) o.bullet = true;
    else if (el.bullet && typeof el.bullet === "object") {
      o.bullet =
        el.bullet.type === "number"
          ? { type: "number", ...(el.bullet.indent !== undefined ? { indent: el.bullet.indent } : {}) }
          : el.bullet.indent !== undefined
          ? { indent: el.bullet.indent }
          : true;
    }
  }
  return o;
}

/** DSL text/runs → what addText accepts (string, or [{text, options}]). */
function textContent(el: any): any {
  if (Array.isArray(el.runs)) {
    return el.runs.map((r: any) => {
      const { text, ...rest } = r;
      return { text: String(text ?? ""), options: rest };
    });
  }
  return String(el.text ?? "");
}

function boxOf(el: any): any {
  return { x: el.x, y: el.y, w: el.w, h: el.h };
}

function addTextEl(slide: any, el: any): void {
  slide.addText(textContent(el), { ...boxOf(el), ...textOptions(el) });
}

function addShapeEl(slide: any, el: any): void {
  const shape = SHAPE_MAP[String(el.shape)] ?? "rect";
  const opts: any = { ...boxOf(el) };
  if (el.fill) {
    opts.fill = { color: el.fill.color };
    if (el.fill.transparency !== undefined) opts.fill.transparency = el.fill.transparency;
  }
  if (el.line) {
    opts.line = { color: el.line.color };
    if (el.line.width !== undefined) opts.line.width = el.line.width;
    if (el.line.dashType) opts.line.dashType = el.line.dashType;
  }
  if (el.rectRadius !== undefined) opts.rectRadius = el.rectRadius;
  if (el.rotate !== undefined) opts.rotate = el.rotate;
  if (el.shadow) opts.shadow = shadowOf(el.shadow);

  const hasLabel = el.text !== undefined || Array.isArray(el.runs);
  if (hasLabel) {
    // Label centered inside the shape: route through addText with a shape option.
    const tOpts = textOptions(el);
    if (tOpts.align === undefined) tOpts.align = "center";
    if (tOpts.valign === undefined) tOpts.valign = "middle";
    slide.addText(textContent(el), { shape, ...opts, ...tOpts });
  } else {
    slide.addShape(shape, opts);
  }
}

function addImageEl(slide: any, el: any): void {
  const src = String(el.src ?? "");
  const opts: any = { ...boxOf(el) };
  if (src.startsWith("data:")) opts.data = src;
  else opts.path = src;
  if (el.sizing) opts.sizing = { type: el.sizing, w: el.w, h: el.h };
  if (el.rounding !== undefined) opts.rounding = el.rounding;
  if (el.rotate !== undefined) opts.rotate = el.rotate;
  if (el.shadow) opts.shadow = shadowOf(el.shadow);
  if (el.alt) opts.altText = el.alt;
  slide.addImage(opts);
}

function addTableEl(slide: any, el: any): void {
  const rows = (el.rows as any[][]).map((row, r) =>
    row.map((cell) => {
      const c = cell && typeof cell === "object" ? cell : { text: String(cell ?? "") };
      const options: any = {};
      // Header styling applies to row 0 when header:true; per-cell props win.
      if (el.header !== false && r === 0) {
        options.fill = { color: el.headerFill ?? "1F2937" };
        options.color = el.headerColor ?? "FFFFFF";
        options.bold = true;
      }
      if (c.fill) options.fill = { color: c.fill };
      if (c.color) options.color = c.color;
      if (c.align) options.align = c.align;
      if (c.bold !== undefined) options.bold = c.bold;
      if (c.colspan !== undefined) options.colspan = c.colspan;
      if (c.rowspan !== undefined) options.rowspan = c.rowspan;
      return { text: String(c.text ?? ""), options };
    })
  );
  const opts: any = { ...boxOf(el) };
  if (el.colW) opts.colW = el.colW;
  if (el.fontSize !== undefined) opts.fontSize = el.fontSize;
  if (el.border) opts.border = { type: "solid", color: el.border.color ?? "CBD5E1", pt: el.border.pt ?? 1 };
  slide.addTable(rows, opts);
}

function addChartEl(slide: any, el: any, pptx: any): void {
  const type = CHART_MAP[String(el.chart)] ?? "bar";
  const categories = Array.isArray(el.categories) ? el.categories : [];
  const data = (el.series ?? []).map((s: any) => ({
    name: s.name,
    labels: categories,
    values: s.values,
  }));
  const opts: any = { ...boxOf(el) };
  if (el.chartColors) opts.chartColors = el.chartColors;
  if (el.title) {
    opts.title = el.title;
    opts.showTitle = true;
  }
  if (el.barDir) opts.barDir = el.barDir;
  if (el.showLegend !== undefined) opts.showLegend = el.showLegend;
  if (el.legendPos) opts.legendPos = el.legendPos;
  if (el.showValue !== undefined) opts.showValue = el.showValue;
  if (el.dataLabelColor) opts.dataLabelColor = el.dataLabelColor;
  if (el.dataLabelPosition) opts.dataLabelPosition = el.dataLabelPosition;
  if (el.catAxisLabelColor) opts.catAxisLabelColor = el.catAxisLabelColor;
  if (el.valAxisLabelColor) opts.valAxisLabelColor = el.valAxisLabelColor;
  if (el.valGridLine) opts.valGridLine = el.valGridLine;
  if (el.catGridLine === "none") opts.catGridLine = { style: "none" };
  if (el.lineSmooth !== undefined) opts.lineSmooth = el.lineSmooth;
  if (el.chartArea) opts.chartArea = el.chartArea;
  const chartType = (pptx?.ChartType && pptx.ChartType[type]) ?? type;
  slide.addChart(chartType, data, opts);
}

/** Render DSL slides into a complete .pptx, returned as a base64 string. */
export async function renderSlidesToBase64(slides: DslSlide[]): Promise<string> {
  const pptx: any = new (PptxGenJS as any)();
  pptx.defineLayout({ name: LAYOUT_NAME, width: 13.333, height: 7.5 });
  pptx.layout = LAYOUT_NAME;

  for (const sl of slides) {
    const slide = pptx.addSlide();
    if (sl.background?.color) {
      slide.background = { color: sl.background.color };
    } else if (sl.background?.image) {
      const img = String(sl.background.image);
      slide.background = img.startsWith("data:") ? { data: img } : { path: img };
    }
    for (const el of sl.elements ?? []) {
      switch (el?.type) {
        case "text":
          addTextEl(slide, el);
          break;
        case "shape":
          addShapeEl(slide, el);
          break;
        case "image":
          addImageEl(slide, el);
          break;
        case "table":
          addTableEl(slide, el);
          break;
        case "chart":
          addChartEl(slide, el, pptx);
          break;
        default:
          // Unknown element types are skipped rather than failing the deck.
          break;
      }
    }
    if (sl.notes) slide.addNotes(String(sl.notes));
  }

  return (await pptx.write({ outputType: "base64" })) as string;
}
